'use client';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useState, useRef } from 'react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { checkBoxFormFields } from './formhelper';
import ThankyouMessage from './thank-you';

import { onest } from '@/styles/fonts';
import { phoneSchema } from '@/lib/utils';
import Link from 'next/link';

const formSchema = z.object({
  firstName: z.string().min(1, { error: 'First name is required' }),
  lastName: z.string().min(1, { error: 'Last name is required' }),
  companyName: z.string(),
  email: z.email({ error: 'Invalid email address' }),
  phoneNumber: phoneSchema,
  projectDetails: z.string().min(10, { error: 'Project details are required' }),
  contactPreference: z
    .array(z.string())
    .refine((value) => value.some((item) => item), {
      error: 'You have to select at least one item.',
    }),
  turnaround: z.string().min(1, { error: 'Turnaround time is required' }),
  howDidYouHear: z
    .string()
    .min(1, { error: 'How did you hear about us is required' }),
});

export const QuoteForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      phoneNumber: '',
      projectDetails: '',
      contactPreference: [],
      turnaround: '',
      howDidYouHear: '',
    },
  });

  const { isSubmitting } = form.formState;

  const handleScroll = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' });
      window.scrollTo(0, 0);
    }
  };

  /**
   * Collects form data and sends it to monday via monday.api
   */
  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const res = await fetch('/api/monday/create-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        throw new Error('Failed to create quote request');
      }

      const resData = await res.json();
      if (!resData || !resData.data || !resData.data.create_item) {
        throw new Error('Unexpected response from Monday API');
      }

      toast.success('Quote request submitted successfully', { duration: 4000 });
      form.reset();
      setIsSubmitted(true);
      handleScroll();
    } catch (error: unknown) {
      let errorMessage = 'Failed to create quote request';
      if (error)
        errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create quote request!';
      toast.error(errorMessage, { duration: 4000 });
      setIsSubmitted(false);
    }
  };

  return (
    <div
      ref={targetRef}
      className={`${onest.className} mx-auto max-w-[950px] rounded-3xl bg-rm-neutral-700 px-6 py-[35px] xm:px-20 lg:min-w-[960px]`}
    >
      {isSubmitted ? (
        <ThankyouMessage setIsFormSubmitted={setIsSubmitted} />
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex w-full flex-col gap-6 md:flex-row">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="flex flex-col md:flex-1">
                    <FormLabel className="text-[1rem] font-semibold text-white">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <input
                        placeholder="John"
                        {...field}
                        className="rounded-full px-6 py-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="flex flex-col md:flex-1">
                    <FormLabel className="text-[1rem] font-semibold text-white">
                      Last Name
                    </FormLabel>
                    <FormControl>
                      <input
                        placeholder="Smith"
                        {...field}
                        className="rounded-full px-6 py-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel className="text-[1rem] font-semibold text-white">
                    Company Name
                  </FormLabel>
                  <FormControl>
                    <input
                      placeholder="Company ABC"
                      {...field}
                      className="rounded-full px-6 py-2"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex w-full flex-col gap-6 md:flex-row">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex flex-col md:flex-1">
                    <FormLabel className="text-[1rem] font-semibold text-white">
                      Email
                    </FormLabel>
                    <FormControl>
                      <input
                        placeholder="email@example.com"
                        {...field}
                        className="rounded-full px-6 py-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="flex flex-1 flex-col">
                    <FormLabel className="text-[1rem] font-semibold text-white">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <input
                        placeholder={'(123) 456 - 7897'}
                        className="rounded-full px-6 py-2"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const digits = value.replace(/\D/g, ''); // Remove non-numeric characters
                          // Handle dynamic formatting
                          const formatted =
                            digits.length > 0
                              ? `(${digits.slice(0, 3)}${
                                  digits.length > 3
                                    ? `) ${digits.slice(3, 6)}${
                                        digits.length > 6
                                          ? ` - ${digits.slice(6, 10)}`
                                          : ''
                                      }`
                                    : ''
                                }`
                              : ''; // Allow empty value
                          field.onChange(formatted);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="projectDetails"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[1rem] font-semibold text-white">
                    Project Details
                  </FormLabel>
                  <FormControl aria-label={'project details textarea'}>
                    <Textarea
                      placeholder="Please provide project details here"
                      className="max-h-[300px] min-h-[150px] resize-y rounded-[21px] md:min-h-[170px] lg:min-h-[250px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="turnaround"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[1rem] font-semibold text-white">
                    Project Turnaround
                  </FormLabel>
                  <FormControl aria-label={'project details textarea'}>
                    <FormControl className="w-full">
                      <input
                        placeholder="3 - 5 Business Days"
                        {...field}
                        className="rounded-full px-6 py-2"
                      />
                    </FormControl>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex w-full flex-col md:flex-row md:gap-6">
              <FormField
                control={form.control}
                name="contactPreference"
                render={() => (
                  <FormItem className="mb-4 flex-col text-white md:flex-1">
                    <div className="text-[1rem] font-semibold text-white">
                      Preferred Contact Method
                    </div>
                    <div className="mt-4 flex w-full items-center justify-between md:justify-start md:gap-5 lg:gap-14">
                      {checkBoxFormFields.map((item) => (
                        <FormField
                          key={item.id}
                          control={form.control}
                          name="contactPreference"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={item.id}
                                className="flex items-center"
                              >
                                <FormControl
                                  aria-label={`${item.name} checkbox`}
                                >
                                  <Checkbox
                                    checked={field.value?.includes(item.id)}
                                    className="mt-0 border-white bg-white"
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([
                                            ...field.value,
                                            item.id,
                                          ])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== item.id
                                            )
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="pl-2 font-normal">
                                  {item.name}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="howDidYouHear"
                render={({ field }) => (
                  <FormItem className="md:flex-1">
                    <FormLabel className="w-full text-[1rem] font-semibold text-white">
                      How Did You Hear About Us?
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger
                          className="rounded-full text-muted-foreground"
                          aria-label="How did you hear about us?"
                        >
                          <SelectValue placeholder="Select Option" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Business Referral">
                          Business Referral
                        </SelectItem>
                        <SelectItem value="Google">Google</SelectItem>
                        <SelectItem value="Mail Ad">Mail Ad</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-center justify-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-12 w-full max-w-[305px] cursor-pointer rounded-full border-2 border-white bg-rm-neutral-700 px-6 py-3 text-[1rem] font-semibold text-white lg:hover:bg-white/90 lg:hover:text-rm-neutral-700"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
            <div>
              <p className="text-center text-[0.875rem] text-white">
                By Submitting this form you agree to our{' '}
                <Link
                  href={'/terms-of-service'}
                  className="text-nowrap font-bold underline underline-offset-4"
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  href={'/privacy-policy'}
                  className="text-nowrap font-bold underline underline-offset-4"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
};
